/**
 * Bulk operations (ADR-0046): one action on many sites, run as one thing —
 * followed together, stopped together, retried together.
 *
 * Instant ones only ask for a look sooner (SEO, a picture, the platform) and
 * are done at once. The rest are the site's own jobs, launched a few at a
 * time. A risky one — anything that touches a working site — goes to one site
 * first, the canary, and only after it succeeds to the rest; two failures
 * stop what has not started.
 */

export const BULK_OPERATIONS = {
  seo_check: { label: 'Проверить SEO', instant: true, risky: false, parallel: 0 },
  preview: { label: 'Обновить снимок', instant: true, risky: false, parallel: 0 },
  platform_detect: { label: 'Определить платформу', instant: true, risky: false, parallel: 0 },
  // Asking for the connection or a read is instant: the worker does the rest (ADR-0049, ADR-0050).
  search_console: { label: 'Подключить к Search Console', instant: true, risky: false, parallel: 0 },
  seo_metrics: { label: 'Обновить SEO-данные', instant: true, risky: false, parallel: 0 },
  backup: { label: 'Создать копию', instant: false, risky: false, parallel: 3 },
  restart: { label: 'Перезапустить', instant: false, risky: true, parallel: 2 },
  // A template version rolled out to the template's sites (ADR-0047): each is
  // an update with a backup first and its own rollback, so the canary and the
  // stop after two failures guard it rather than a small limit.
  template_upgrade: { label: 'Обновить до версии шаблона', instant: false, risky: true, parallel: 2 },
} as const satisfies Record<string, { label: string; instant: boolean; risky: boolean; parallel: number }>;

/** Risky actions a person confirms by typing the number of sites, and keeps to ten. */
export const BULK_CONFIRMED_BY_COUNT: ReadonlySet<BulkOperation> = new Set(['restart']);

export type BulkOperation = keyof typeof BULK_OPERATIONS;
export const BULK_OPERATION_NAMES = Object.keys(BULK_OPERATIONS) as [BulkOperation, ...BulkOperation[]];

/** How many sites one run may take: a risky action is kept to a number a person can still follow. */
export const BULK_LIMITS = { sites: 200, risky: 10, stopAfterFailures: 2 } as const;

export const BULK_ITEM_STATES = ['pending', 'queued', 'done', 'failed', 'skipped', 'cancelled'] as const;
export type BulkItemState = (typeof BULK_ITEM_STATES)[number];

/**
 * What a run may launch now, given where its items stand: nothing when it
 * should stop; one — the canary — until a risky action has succeeded once;
 * up to its parallel limit otherwise.
 */
export function bulkNext(operation: BulkOperation, states: readonly BulkItemState[]): { launch: number; stop: boolean } {
  const meta = BULK_OPERATIONS[operation];
  const count = (state: BulkItemState) => states.filter((item) => item === state).length;
  const failed = count('failed');
  if (meta.risky && failed >= BULK_LIMITS.stopAfterFailures) return { launch: 0, stop: true };
  const pending = count('pending');
  const inFlight = count('queued');
  if (pending === 0) return { launch: 0, stop: false };
  // The canary: one alone, and the rest wait for it to succeed.
  if (meta.risky && count('done') === 0) {
    if (failed > 0) return { launch: 0, stop: true };
    return { launch: inFlight === 0 ? 1 : 0, stop: false };
  }
  return { launch: Math.max(0, Math.min(pending, meta.parallel - inFlight)), stop: false };
}
